import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  getUnitEconomicsSummary,
  getUnitEconomicsExpenses,
  addUnitEconomicsExpense,
  getUnitEconomicsCategories,
  recalculateUnitEconomics,
} from '../api';

interface ProjectExpensesTabProps {
  projectId: number;
}

const currencyFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
});

export const ProjectExpensesTab: React.FC<ProjectExpensesTabProps> = ({ projectId }) => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [summary, setSummary] = useState<any>(null);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [open, setOpen] = useState(false);
  const [newExpense, setNewExpense] = useState({
    categoryId: '',
    amount: '',
    description: '',
  });

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [summaryData, expensesData, categoriesData] = await Promise.all([
        getUnitEconomicsSummary(projectId, month),
        getUnitEconomicsExpenses(projectId, month),
        getUnitEconomicsCategories(),
      ]);
      setSummary(summaryData);
      setExpenses(expensesData);
      setCategories(categoriesData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [projectId, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRecalculate = async () => {
    setLoading(true);
    try {
      await recalculateUnitEconomics(projectId, month);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Recalculation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async () => {
    try {
      await addUnitEconomicsExpense({
        ...newExpense,
        projectId,
        period: month,
        categoryId: Number(newExpense.categoryId),
        amount: Number(newExpense.amount),
      });
      setOpen(false);
      setNewExpense({ categoryId: '', amount: '', description: '' });
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to add expense');
    }
  };

  if (!projectId) return <Typography>Please select a project</Typography>;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <TextField
          label="Период"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          size="small"
        />
        <Button variant="contained" color="primary" onClick={handleRecalculate} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Пересчитать месяц'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Metrics Dashboard */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Средний CPL</Typography>
              <Typography variant="h5">{summary ? currencyFormatter.format(summary.averageCPL) : '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Всего затрат</Typography>
              <Typography variant="h5">{summary ? currencyFormatter.format(summary.totalExpenses) : '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Чистая прибыль</Typography>
              <Typography variant="h5" color={summary?.profitability >= 0 ? 'success.main' : 'error.main'}>
                {summary ? currencyFormatter.format(summary.profitability) : '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Breakdown Table */}
      <Typography variant="h6" gutterBottom>Детализация затрат</Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Категория</TableCell>
              <TableCell>Тип затрат</TableCell>
              <TableCell align="right">Сумма</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary?.breakdown.map((item: any, idx: number) => (
              <TableRow key={idx}>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.type}</TableCell>
                <TableCell align="right">{currencyFormatter.format(item.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Expense Log */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Журнал расходов</Typography>
        <Button variant="outlined" onClick={() => setOpen(true)}>Добавить расход</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Категория</TableCell>
              <TableCell>Описание</TableCell>
              <TableCell align="right">Сумма</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.map((expense) => (
              <TableRow key={expense.id}>
                <TableCell>{expense.category_name}</TableCell>
                <TableCell>{expense.description}</TableCell>
                <TableCell align="right">{currencyFormatter.format(expense.amount)}</TableCell>
              </TableRow>
            ))}
            {expenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">Расходов не найдено</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Expense Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Добавить расход</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Категория</InputLabel>
            <Select
              value={newExpense.categoryId}
              label="Категория"
              onChange={(e) => setNewExpense({ ...newExpense, categoryId: e.target.value as string })}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Сумма"
            type="number"
            value={newExpense.amount}
            onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Описание"
            multiline
            rows={2}
            value={newExpense.description}
            onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button onClick={handleAddExpense} variant="contained" disabled={!newExpense.categoryId || !newExpense.amount}>
            Добавить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

